import React from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import WifiTetheringIcon from "@mui/icons-material/WifiTethering";
import { NEARBY_TRANSFER_STATES } from "../nearbyTransfer.constants";

const STATUS_LABELS = Object.freeze({
  [NEARBY_TRANSFER_STATES.OFFLINE]: "Offline",
  [NEARBY_TRANSFER_STATES.CONNECTING]: "Connecting",
  [NEARBY_TRANSFER_STATES.DISCOVERING]: "Discovering",
  [NEARBY_TRANSFER_STATES.REQUESTING_PAIR]: "Pairing request sent",
  [NEARBY_TRANSFER_STATES.INCOMING_PAIR]: "Pairing request",
  [NEARBY_TRANSFER_STATES.PAIRING]: "Pairing",
  [NEARBY_TRANSFER_STATES.CONNECTED]: "Connected",
  [NEARBY_TRANSFER_STATES.TRANSFERRING]: "Transferring",
  [NEARBY_TRANSFER_STATES.FAILED]: "Needs attention",
});

const PROGRESS_WIDTH_CLASSES = [
  "w-0",
  "w-[10%]",
  "w-[20%]",
  "w-[30%]",
  "w-[40%]",
  "w-[50%]",
  "w-[60%]",
  "w-[70%]",
  "w-[80%]",
  "w-[90%]",
  "w-full",
];

const getProgressWidthClass = (progress) => {
  const index = Math.min(Math.max(Math.ceil(progress / 10), 0), 10);
  return PROGRESS_WIDTH_CLASSES[index];
};

export const PanelButton = ({
  children,
  disabled,
  icon,
  onClick,
  tone = "primary",
}) => {
  const toneClass =
    tone === "primary"
      ? "bg-primary text-on-primary hover:bg-primary-hover"
      : "border border-border bg-surface text-text hover:bg-surface-muted";

  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-xs font-semibold ${toneClass} disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-text-muted`}
      disabled={disabled}
      onClick={onClick}
      type="button"
    >
      {icon}
      {children}
    </button>
  );
};

export const StatusPill = ({ isConnected, status }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${
      isConnected
        ? "bg-success-surface text-success-text"
        : "bg-surface-muted text-text-muted"
    }`}
  >
    {isConnected && <span className="h-2 w-2 rounded-full bg-success" />}
    {STATUS_LABELS[status] ?? "Nearby"}
  </span>
);

export const SavedAttachmentNotice = ({ hasSavedAttachment, hasSelectedFile }) => {
  if (!hasSavedAttachment || hasSelectedFile) return null;

  return (
    <p className="rounded-md bg-warning-surface p-3 text-xs text-warning-text">
      Select the file again to include it in a nearby transfer.
    </p>
  );
};

export const ConnectedDeviceCard = ({
  connectedDevice,
  isTransferring,
  onDisconnect,
  onSend,
  pairingCode,
}) => (
  <div className="rounded-md border border-success bg-success-surface p-3">
    <p className="text-xs font-semibold uppercase text-success-text">Paired with</p>
    <p className="mt-1 font-semibold text-text">
      {connectedDevice?.deviceName ?? "Nearby Netpad"}
    </p>
    {pairingCode && (
      <p className="mt-2 text-xs text-text">
        Confirmation code <span className="font-bold">{pairingCode}</span>
      </p>
    )}
    <div className="mt-3 flex flex-wrap gap-2">
      <PanelButton
        disabled={isTransferring}
        icon={<SendIcon fontSize="small" />}
        onClick={onSend}
      >
        Send current note
      </PanelButton>
      <PanelButton
        icon={<CloseIcon fontSize="small" />}
        onClick={onDisconnect}
        tone="secondary"
      >
        Disconnect
      </PanelButton>
    </div>
  </div>
);

export const IncomingPairCard = ({ request, onAccept, onDecline }) => (
  <div className="rounded-md border border-primary bg-info-surface p-3">
    <p className="text-sm font-semibold text-text">
      {request.fromDeviceName ?? "Nearby Netpad"} wants to pair.
    </p>
    <div className="mt-3 flex flex-wrap gap-2">
      <PanelButton icon={<CheckCircleIcon fontSize="small" />} onClick={onAccept}>
        Accept
      </PanelButton>
      <PanelButton
        icon={<CloseIcon fontSize="small" />}
        onClick={onDecline}
        tone="secondary"
      >
        Decline
      </PanelButton>
    </div>
  </div>
);

export const DeviceList = ({ devices, isPairing, onPair }) => {
  if (!devices.length) {
    return (
      <p className="rounded-md border border-dashed border-border p-3 text-xs text-text-muted">
        No nearby Netpads found yet. Open Netpad on another device on the same
        network.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {devices.map((device) => (
        <li
          className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface p-2"
          key={device.deviceId}
        >
          <span className="min-w-0 truncate text-sm font-semibold text-primary">
            {device.deviceName}
          </span>
          <PanelButton disabled={isPairing} onClick={() => onPair(device)}>
            Pair
          </PanelButton>
        </li>
      ))}
    </ul>
  );
};

export const TransferProgress = ({ progress }) => (
  <div className="rounded-md bg-surface-muted p-3">
    <p className="text-xs font-semibold text-text">
      {progress >= 100 ? "Transfer complete." : `Transferring ${progress}%`}
    </p>
    <div className="mt-2 h-2 overflow-hidden rounded-full bg-border">
      <div className={`h-full rounded-full bg-primary ${getProgressWidthClass(progress)}`} />
    </div>
  </div>
);

export const WaitingCard = ({ text }) => (
  <p className="rounded-md bg-surface-muted p-3 text-xs font-semibold text-text">
    {text}
  </p>
);

export const PanelIcon = () => <WifiTetheringIcon className="text-primary" />;
